import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Clock, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import {
  listSmartOrdersAdmin,
  previewSmartGeneration,
} from "@/lib/bousala/smart-admin.functions";
import {
  SMART_SECTION_KEYS,
  smartReportDate,
  type SmartOrderStatus,
  type SmartSectionKey,
} from "@/lib/bousala/smart-order";
import { SmartReportView } from "@/components/bousala/smart/smart-report-view";
import { DEMO_INTAKE, DEMO_MAJORS } from "@/lib/bousala/smart-demo-data";
import {
  SMART_COUNTRY_LABELS_AR,
  SMART_FINANCIAL_CONTEXT_LABELS_AR,
  smartCountryLabel,
} from "@/lib/bousala/smart-intake-schema";
import { arabicDate, arabicDateTimeDamascus } from "@/lib/arabic-format";

const listQ = queryOptions({
  queryKey: ["admin", "smart", "orders"],
  queryFn: () => listSmartOrdersAdmin(),
});

export const Route = createFileRoute("/_authenticated/admin/bousala/smart/")({
  loader: ({ context }) => context.queryClient.ensureQueryData(listQ),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: SmartQueuePage,
});

export const STATUS_LABELS_AR: Record<SmartOrderStatus, string> = {
  payment_pending_review: "دفع قيد المراجعة",
  paid: "مدفوع",
  assessment_complete: "أنهى الاختبار",
  intake_complete: "جاهز للتوليد",
  generating: "قيد التوليد",
  ready_for_review: "بانتظار المراجعة",
  sent: "أُرسل",
  refunded: "مسترجع",
  stalled: "متوقف",
};

const STATUS_TONE: Record<SmartOrderStatus, string> = {
  payment_pending_review: "bg-amber-100 text-amber-900",
  paid: "bg-sky-100 text-sky-900",
  assessment_complete: "bg-sky-100 text-sky-900",
  intake_complete: "bg-emerald-100 text-emerald-900",
  generating: "bg-violet-100 text-violet-900",
  ready_for_review: "bg-emerald-100 text-emerald-900",
  sent: "bg-slate-100 text-slate-900",
  refunded: "bg-slate-100 text-slate-900",
  stalled: "bg-rose-100 text-rose-900",
};

export function StatusChip({ status }: { status: SmartOrderStatus }) {
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${STATUS_TONE[status]}`}>
      {STATUS_LABELS_AR[status]}
    </span>
  );
}

function timeLeft(slaDueAt: string | null, overdue: boolean): string {
  if (!slaDueAt) return "—";
  const diff = new Date(slaDueAt).getTime() - Date.now();
  const hours = Math.round(Math.abs(diff) / 3600000);
  if (overdue) return `تأخر ${hours} ساعة`;
  if (hours < 1) return "أقل من ساعة";
  return `باقي ${hours} ساعة`;
}

function SmartQueuePage() {
  const { data } = useSuspenseQuery(listQ);
  const [filter, setFilter] = useState<SmartOrderStatus | "all">("all");

  const rows = useMemo(
    () => (filter === "all" ? data.items : data.items.filter((i) => i.status === filter)),
    [data.items, filter],
  );

  const statuses = useMemo(
    () => [...new Set(data.items.map((i) => i.status))] as SmartOrderStatus[],
    [data.items],
  );

  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">البوصلة الذكية</h1>
          <p className="text-sm text-muted-foreground">
            طلبات بحاجة إلى إجراء: {data.needsAction} من {data.items.length}
          </p>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="الكل" />
        {statuses.map((s) => (
          <FilterChip
            key={s}
            active={filter === s}
            onClick={() => setFilter(s)}
            label={STATUS_LABELS_AR[s]}
          />
        ))}
      </div>

      {rows.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            لا توجد طلبات ضمن هذا التصنيف بعد.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => (
            <Link
              key={row.id}
              to="/admin/bousala/smart/$id"
              params={{ id: row.id }}
              className="block rounded-2xl border bg-card p-4 transition hover:border-primary/40"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate font-bold">{row.firstName || "بلا اسم"}</p>
                  <p className="text-xs text-muted-foreground">
                    أُنشئ {arabicDate(row.createdAt)}
                    {row.paymentMethod ? ` · ${row.paymentMethod}` : ""}
                    {row.environment ? ` · ${row.environment}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-bold ${
                      row.overdue ? "text-rose-600" : "text-muted-foreground"
                    }`}
                  >
                    {row.overdue ? <AlertTriangle className="size-4" aria-hidden /> : <Clock className="size-4" aria-hidden />}
                    {timeLeft(row.slaDueAt, row.overdue)}
                  </span>
                  <StatusChip status={row.status} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <TonePreview />
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-full px-4 py-2 text-xs font-bold transition ${
        active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/70 hover:bg-muted/70"
      }`}
    >
      {label}
    </button>
  );
}

function TonePreview() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [intake, setIntake] = useState({
    familyExpectations: DEMO_INTAKE.family_expectations,
    futureGoals: DEMO_INTAKE.future_goals,
    concerns: DEMO_INTAKE.concerns,
  });
  const [majorIds, setMajorIds] = useState<string[]>([...DEMO_INTAKE.considered_major_ids]);
  const [lang, setLang] = useState<"ar" | "en">("ar");
  const [sections, setSections] = useState<Partial<Record<SmartSectionKey, string>> | null>(null);

  const toggleMajor = (id: string) =>
    setMajorIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 3 ? prev : [...prev, id],
    );

  async function run() {
    setBusy(true);
    setSections(null);
    try {
      const res = await previewSmartGeneration({
        data: {
          targetCountry: DEMO_INTAKE.target_country,
          financialContext: DEMO_INTAKE.financial_context,
          consideredMajorIds: majorIds,
          lang,
          ...intake,
        },
      });
      if (!res.ok) toast.error(res.reason);
      else setSections(res.sections);
    } catch (e: any) {
      toast.error(e?.message ?? "تعذّر التوليد.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="border-dashed">
      <CardContent className="space-y-4 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 font-display text-lg font-bold">
              <Sparkles className="size-4" aria-hidden />
              معاينة تجريبية
            </h2>
            <p className="text-sm text-muted-foreground">
              توليد على نتيجتك أنت، بلا أي حفظ — للاطمئنان على النبرة قبل الإطلاق.
            </p>
          </div>
          <Button variant="outline" onClick={() => setOpen((v) => !v)}>
            {open ? "إخفاء" : "فتح"}
          </Button>
        </div>

        {open ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm font-bold">لغة التقرير</p>
              <div className="flex gap-2">
                {(["ar", "en"] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    aria-pressed={lang === k}
                    onClick={() => setLang(k)}
                    className={`min-h-10 rounded-full px-4 text-sm font-bold ${
                      lang === k ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {k === "ar" ? "العربية" : "English"}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-bold">التخصصات التي يفكر فيها (حتى ثلاثة)</p>
              <div className="flex max-h-40 flex-wrap gap-2 overflow-y-auto rounded-2xl border p-3">
                {DEMO_MAJORS.map((m) => {
                  const on = majorIds.includes(m.id);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      aria-pressed={on}
                      onClick={() => toggleMajor(m.id)}
                      className={`rounded-full px-3 py-1 text-xs font-bold ${
                        on ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {m.name_ar}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid gap-3">
              <Field
                label="توقعات الأهل"
                value={intake.familyExpectations}
                onChange={(v) => setIntake((s) => ({ ...s, familyExpectations: v }))}
              />
              <Field
                label="أهدافه المستقبلية"
                value={intake.futureGoals}
                onChange={(v) => setIntake((s) => ({ ...s, futureGoals: v }))}
              />
              <Field
                label="مخاوفه"
                value={intake.concerns}
                onChange={(v) => setIntake((s) => ({ ...s, concerns: v }))}
              />
            </div>
            <Button onClick={run} disabled={busy}>
              {busy ? <Loader2 className="ms-2 size-4 animate-spin" aria-hidden /> : null}
              توليد المعاينة
            </Button>

            {sections ? (
              <div className="rounded-2xl border p-3">
                <SmartReportView
                  header={{
                    clientName: "معاينة",
                    code: "—",
                    deliveredAt: smartReportDate(new Date(), lang),
                    consideredMajors: DEMO_MAJORS.filter((m) => majorIds.includes(m.id)).map(
                      (m) => m.name_ar,
                    ),
                    targetCountry: smartCountryLabel(DEMO_INTAKE.target_country, lang),
                  }}
                  sections={sections}
                  lang={lang}
                />
              </div>
            ) : null}
            <p className="text-xs text-muted-foreground">
              السياق المالي المستخدم:{" "}
              {SMART_FINANCIAL_CONTEXT_LABELS_AR[
                DEMO_INTAKE.financial_context as keyof typeof SMART_FINANCIAL_CONTEXT_LABELS_AR
              ] ?? DEMO_INTAKE.financial_context}
              {" · "}
              الأقسام: {SMART_SECTION_KEYS.length}
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-bold">{label}</span>
      <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} />
    </label>
  );
}
